import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlinx.coroutines.launch

// Ligové zápasy: výběr sezóny -> výběr zápasu (kolo + soupeř) -> hry hráčů týmu (z bowlingliga
// duelů) s možností přidat kompletní zápis. Ligové skóre je z bowlingligy (autoritativní, read-only);
// zápis se ukládá do sg_scoresheet (source_kind='league', bez zásahu do bowlingligy).

private data class LeagueGame(val blPlayerId: String, val gameNo: Int, val leagueScore: Int?)

/** Z lineup + duelů poskládá hry hráčů týmu (3 hry/hráč, game_no dle pořadí sekvence). */
private fun computeLeagueGames(lineup: List<BlLineupDto>, duels: List<BlDuelDto>, teamIsHome: Boolean): List<LeagueGame> {
    val posToPlayer = lineup.associate { it.position to it.playerId }
    val byPlayer = linkedMapOf<String, MutableList<Pair<Int, Int?>>>() // player -> (sequence, score)
    for (d in duels.sortedBy { it.sequence }) {
        val pos = if (teamIsHome) d.homePosition else d.awayPosition
        val pid = posToPlayer[pos] ?: continue
        val score = if (teamIsHome) d.homeScore else d.awayScore
        byPlayer.getOrPut(pid) { mutableListOf() }.add(d.sequence to score)
    }
    return byPlayer.flatMap { (pid, list) ->
        list.sortedBy { it.first }.mapIndexed { idx, (_, score) -> LeagueGame(pid, idx + 1, score) }
    }
}

@Composable
fun LeagueSection(token: String, member: SgMemberDto?, profiles: List<SgProfileDto>, readOnly: Boolean = false) {
    val nameById = remember(profiles) { profiles.associate { it.blPlayerId to it.displayName } }
    var seasons by remember { mutableStateOf<List<Pair<String, String>>>(emptyList()) }
    var selectedSeasonId by remember { mutableStateOf<String?>(null) }
    var matches by remember { mutableStateOf<List<TeamMatchDto>>(emptyList()) }
    var selectedMatchId by remember { mutableStateOf<String?>(null) }
    var games by remember { mutableStateOf<List<LeagueGame>>(emptyList()) }
    val sheets = remember { mutableStateMapOf<String, List<FrameDto>>() }
    var editorTarget by remember { mutableStateOf<EditorTarget?>(null) }
    var loading by remember { mutableStateOf(true) }
    var loadingMatch by remember { mutableStateOf(false) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        try {
            val hist = Api.fetchTeamPlayerHistory()
            seasons = hist.players.flatMap { it.seasons }.map { it.seasonId to it.seasonName }.distinct().sortedByDescending { it.second }
            selectedSeasonId = seasons.firstOrNull()?.first
        } catch (e: Throwable) {
            error = e.apiMessage()
        } finally {
            loading = false
        }
    }

    LaunchedEffect(selectedSeasonId) {
        val sid = selectedSeasonId ?: return@LaunchedEffect
        try {
            matches = Api.fetchTeamMatches(sid).filter { it.status == "completed" }
            selectedMatchId = null; games = emptyList(); sheets.clear()
        } catch (e: Throwable) {
            error = e.apiMessage()
        }
    }

    LaunchedEffect(selectedMatchId) {
        val mid = selectedMatchId ?: return@LaunchedEffect
        val match = matches.find { it.id == mid } ?: return@LaunchedEffect
        loadingMatch = true; message = null; sheets.clear()
        try {
            val lineup = Api.fetchBlMatchLineup(mid)
            val duels = Api.fetchBlMatchDuels(mid)
            games = computeLeagueGames(lineup, duels, match.home)
            Api.fetchLeagueSheets(token, mid).forEach { sheets["${it.blPlayerId}#${it.gameNo}"] = it.frames }
        } catch (e: Throwable) {
            error = e.apiMessage()
        } finally {
            loadingMatch = false
        }
    }

    Div(attrs = { classes("card") }) {
        H3 { Text("Ligové zápasy") }
        P(attrs = { classes("muted") }) { Text("Ligové skóre je z bowlingligy (nemění se). Kompletní zápis se přidává jako doplněk pro statistiky.") }

        when {
            loading -> P { Text("Načítám…") }
            error != null -> P { Text("Chyba: $error") }
            else -> {
                Div(attrs = { classes("training-meta") }) {
                    Label {
                        Text("Sezóna")
                        Select(attrs = { classes("field"); onChange { ev -> ev.value?.let { selectedSeasonId = it } } }) {
                            seasons.forEach { (id, name) ->
                                Option(id, attrs = { if (selectedSeasonId == id) attr("selected", "selected") }) { Text(name) }
                            }
                        }
                    }
                    Label {
                        Text("Zápas")
                        Select(attrs = { classes("field"); onChange { ev -> selectedMatchId = ev.value?.ifBlank { null } } }) {
                            Option("", attrs = { if (selectedMatchId == null) attr("selected", "selected") }) { Text("— vyber zápas —") }
                            matches.forEach { m ->
                                Option(m.id ?: "", attrs = { if (selectedMatchId == m.id) attr("selected", "selected") }) {
                                    Text("${m.roundNumber ?: "?"}. kolo · ${m.opponent} (${if (m.home) "doma" else "venku"})")
                                }
                            }
                        }
                    }
                }

                when {
                    selectedMatchId == null -> P(attrs = { classes("muted") }) { Text("Vyber zápas pro zobrazení her.") }
                    loadingMatch -> P { Text("Načítám hry…") }
                    else -> LeagueGames(games, nameById, sheets, if (readOnly) null else ({ key -> editorTarget = EditorTarget(key, "classic") }))
                }

                if (!readOnly && selectedMatchId != null && !loadingMatch) {
                    Div(attrs = { classes("form-actions") }) {
                        Button(attrs = {
                            classes("primary-btn")
                            if (busy) disabled()
                            onClick {
                                val createdBy = member?.userId ?: decodeJwtClaims(token)?.sub
                                val mid = selectedMatchId
                                if (createdBy == null || mid == null) { message = "Chybí účet/zápas."; return@onClick }
                                val toSave = sheets.mapNotNull { (key, frames) ->
                                    val parts = key.split("#"); val g = parts.getOrNull(1)?.toIntOrNull() ?: return@mapNotNull null
                                    LeagueGameSheet(parts[0], g, frames, Scoring.total("classic", frames.map { it.t }))
                                }
                                busy = true; message = null
                                scope.launch {
                                    try {
                                        Api.deleteLeagueSheets(token, mid)
                                        Api.addLeagueSheets(token, mid, createdBy, toSave)
                                        message = "Zápisy uloženy (${toSave.size})."
                                    } catch (e: Throwable) {
                                        message = "Uložení selhalo: ${e.apiMessage()}"
                                    } finally {
                                        busy = false
                                    }
                                }
                            }
                        }) { Text(if (busy) "Ukládám…" else "Uložit zápisy") }
                    }
                    message?.let { Div(attrs = { classes("form-note") }) { Text(it) } }
                }
            }
        }

        if (!readOnly) editorTarget?.let { target ->
            ScoresheetEditor(
                gameType = "classic",
                initialFrames = sheets[target.key] ?: emptyList(),
                photoImportEnabled = true,
                gameLabel = "${nameById[target.key.substringBefore('#')] ?: "Hráč"} · hra ${target.key.substringAfter('#')}",
                expectedScore = games.find { "${it.blPlayerId}#${it.gameNo}" == target.key }?.leagueScore,
                onSave = { frames, _ -> sheets[target.key] = frames; editorTarget = null },
                onClose = { editorTarget = null },
                onDelete = { sheets.remove(target.key); editorTarget = null },
            )
        }
    }
}

@Composable
private fun LeagueGames(
    games: List<LeagueGame>,
    nameById: Map<String, String>,
    sheets: Map<String, List<FrameDto>>,
    onOpenEditor: ((String) -> Unit)?,
) {
    if (games.isEmpty()) {
        P(attrs = { classes("muted") }) { Text("Zápas nemá zadané duely.") }
        return
    }
    Div(attrs = { classes("league-games") }) {
        games.groupBy { it.blPlayerId }.forEach { (pid, list) ->
            Div(attrs = { classes("league-player") }) {
                Div(attrs = { classes("league-player-name") }) { Text(nameById[pid] ?: pid) }
                Div(attrs = { classes("league-game-row") }) {
                    list.sortedBy { it.gameNo }.forEach { g ->
                        val key = "$pid#${g.gameNo}"
                        val frames = sheets[key]
                        val hasSheet = frames != null
                        val sheetTotal = frames?.let { Scoring.total("classic", it.map { fr -> fr.t }) }
                        Div(attrs = { classes("league-game") }) {
                            Span(attrs = { classes("league-game-no") }) { Text("hra ${g.gameNo}") }
                            Span(attrs = { classes("league-game-score") }) { Text("${g.leagueScore ?: "–"}") }
                            if (hasSheet && sheetTotal != null && sheetTotal != g.leagueScore) {
                                Span(attrs = { classes("league-mismatch"); attr("title", "Součet zápisu ($sheetTotal) ≠ ligové skóre") }) { Text("≠$sheetTotal") }
                            }
                            if (onOpenEditor != null) {
                                Button(attrs = {
                                    classes(*(listOfNotNull("sheet-btn", "sheet-btn-on".takeIf { hasSheet }).toTypedArray()))
                                    attr("title", "Kompletní zápis"); attr("aria-label", "Kompletní zápis")
                                    onClick { onOpenEditor(key) }
                                }) { Text("📝") }
                            }
                        }
                    }
                }
            }
        }
    }
}
